import { guessTwoSpeakerFaceMap } from '@src/services/framing/guessTwoSpeakerFaceMap';
import { withDeducedFaces } from '@src/services/framing/withDeducedFaces';
import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, observable, runInAction } from 'mobx';

import type { Result } from '@src/services/common/Result';
import type { TranscriptWord } from '@src/services/framing/speakerIntervalUtils';
import type {
  FaceDetection,
  FaceDetectionsResponse,
} from '@src/services/framing/types';

const logger = LogManager.getLogger('stores:SpeakerFaceMap');

export type SpeakerFaceMapLoader = (
  storyId: string,
  faceData?: FaceDetectionsResponse | null,
) => Promise<Result<Record<string, string[]>>>;

/**
 * The open story's transcript `speaker_label` → `face_label`s map, loaded once
 * per story so editor commands that create scenes mid-edit (transcript
 * restore, trim-extend) can frame by speaker synchronously, without a fetch
 * per edit.
 */
export class SpeakerFaceMapStore {
  storyId: string | null = null;
  /** Null until loaded, or when the story has no usable mapping. */
  map: Record<string, string[]> | null = null;

  private inFlight: Promise<void> | null = null;
  /**
   * The story's face detections, kept for `mapForFraming`'s guess and the
   * reframe modal. Observable (by reference), so an observer that read it
   * before the story's face data arrived re-renders when it does.
   */
  private detections: ReadonlyArray<FaceDetection> | null = null;
  private guess: {
    transcript: ReadonlyArray<TranscriptWord>;
    map: Record<string, string[]> | null;
  } | null = null;
  /** `mapForFraming`'s last result, recomputed only when an input changes. */
  private deduced: {
    base: Record<string, string[]> | null;
    transcript: ReadonlyArray<TranscriptWord>;
    detections: ReadonlyArray<FaceDetection>;
    map: Record<string, string[]> | null;
  } | null = null;

  constructor(private loadMap: SpeakerFaceMapLoader) {
    makeAutoObservable<
      this,
      'loadMap' | 'inFlight' | 'detections' | 'guess' | 'deduced'
    >(this, {
      loadMap: false,
      inFlight: false,
      detections: observable.ref,
      guess: false,
      deduced: false,
      map: observable.ref,
      mapForFraming: false,
    });
  }

  /** The face detections passed to `load`, or null. */
  get faceDetections(): ReadonlyArray<FaceDetection> | null {
    return this.detections;
  }

  /**
   * `map`, or — when the story has no Face Mapping — a guessed two-speaker
   * map (`guessTwoSpeakerFaceMap`), plus the faces framing can deduce for
   * speakers nobody mapped (`withDeducedFaces`, PLAT-12143). Computed once
   * per input. Needs the face data passed to `load`.
   */
  mapForFraming(
    transcript: ReadonlyArray<TranscriptWord> | undefined,
  ): Record<string, string[]> | null {
    if (!transcript?.length || !this.detections?.length) return this.map;
    const detections = this.detections;
    let base = this.map;
    if (!base) {
      if (this.guess?.transcript !== transcript) {
        const guessed = guessTwoSpeakerFaceMap(transcript, detections);
        this.guess = { transcript, map: guessed.ok ? guessed.value : null };
      }
      base = this.guess.map;
    }
    if (
      this.deduced?.base !== base ||
      this.deduced.transcript !== transcript ||
      this.deduced.detections !== detections
    ) {
      const { map } = withDeducedFaces(base ?? {}, transcript, detections);
      this.deduced = {
        base,
        transcript,
        detections,
        map: Object.keys(map).length > 0 ? map : null,
      };
    }
    return this.deduced.map;
  }

  /**
   * Loads `storyId`'s map unless it is already loaded or loading. Pass the
   * story's already-fetched `faceData` to avoid downloading it again.
   */
  load(
    storyId: string,
    faceData?: FaceDetectionsResponse | null,
  ): Promise<void> {
    if (this.storyId === storyId && (this.map || this.inFlight)) {
      return this.inFlight ?? Promise.resolve();
    }

    this.storyId = storyId;
    this.map = null;
    this.detections = faceData?.detections ?? null;
    this.guess = null;
    this.deduced = null;
    const request = this.loadMap(storyId, faceData).then(result => {
      runInAction(() => {
        if (this.storyId !== storyId) return;
        this.inFlight = null;
        if (result.ok) {
          this.map = result.value;
        } else {
          logger.info('No speaker→face map for story', {
            storyId,
            code: result.error.code,
          });
        }
      });
    });
    this.inFlight = request;
    return request;
  }

  /**
   * Replaces the open story's map with a fresher one — e.g. one rebuilt after
   * the user remapped faces. Ignored for any other story.
   */
  set(storyId: string, map: Record<string, string[]>): void {
    if (storyId !== this.storyId) return;
    this.map = Object.keys(map).length > 0 ? map : null;
  }

  /**
   * Replaces the open story's face detections once they were regenerated
   * (Force Re-run), so the guess and the reframe modal use the new faces.
   * Ignored for any other story.
   */
  setFaceDetections(
    storyId: string,
    detections: ReadonlyArray<FaceDetection>,
  ): void {
    if (storyId !== this.storyId) return;
    this.detections = detections;
    this.guess = null;
  }
}
